import { IModuleProps, Modal, ModalBody, ModalFooter, ModalHeader, Module, Node } from '@msdyn365-commerce-modules/utilities';
import { IContext } from '@msdyn365-commerce/retail-proxy';
import classname from 'classnames';
import React, { FC, ReactNode, createContext, useContext, useEffect, useState } from 'react';

interface ModalProviderNewProps {
    children: ReactNode;
    props: any;
}

type ModalContextType = {
    isOpen: boolean;
    setOpen: (data: ModalProviderContentProps, onClose?: () => void) => void;
    setClose: () => void;
    clearModal: () => void;
    context: IContext;
};

interface ModalProviderContentProps {
    content: ReactNode;
    header?: string | ReactNode;
    size?: 'sm' | 'lg' | 'xl';
}

export const ModalContext = createContext<ModalContextType>({
    isOpen: false,
    setOpen: () => {},
    setClose: () => {},
    clearModal: () => {},
    context: {} as IContext
});

export const ModalProvider: FC<ModalProviderNewProps> = ({ children, props }) => {
    const { context } = props;
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [content, setContent] = useState<ModalProviderContentProps>({ content: null, header: '', size: 'sm' });
    const [isMounted, setIsMounted] = useState<boolean>(false);
    const [onCloseCallback, setOnCloseCallback] = useState<(() => void) | null>(null); // New state to store the close callback

    useEffect(() => {
        setIsMounted(true);
    }, []);

    const setOpen = (data: ModalProviderContentProps, onClose?: () => void) => {
        if (data.content) {
            setContent(data);
            setIsOpen(true);
            if (onClose) {
                setOnCloseCallback(() => onClose);
            }
        }
    };

    const setClose = () => {
        setIsOpen(false);
        clearModal();
        setTimeout(() => {
            setContent({ content: null, header: '', size: 'sm' });
            if (onCloseCallback) {
                onCloseCallback();
            }
            setOnCloseCallback(null);
        }, 150);
    };

    if (!isMounted) {
        return null;
    }

    const modalContainer: IModuleProps = {
        tag: Modal,
        moduleProps: props,
        className: classname({ [`msc-modal__dialog-${content.size}`]: true }),
        isOpen,
        'aria-label': 'open-modal',
        toggle: setClose,
        modalTransition: { timeout: 0 },
        shouldUpdateFocusToFirstElement: true
    };

    const modalHeaderContainer = {
        tag: ModalHeader,
        moduleProps: props,
        className: '',
        toggle: setClose,
        closeButtonTabIndex: 1
    };

    const modalFooterContainer = { tag: ModalFooter, className: '' };

    const modalBodyContainer = { tag: ModalBody, className: '' };

    const clearModal = () => {
        setIsOpen(false);
        setContent({ content: null, header: '', size: 'sm' });
        setOnCloseCallback(null);
    };

    return (
        <ModalContext.Provider value={{ setOpen, setClose, clearModal, isOpen, context }}>
            {children}
            <Module {...modalContainer}>
                <Node {...modalHeaderContainer}>{content.header ?? ''}</Node>
                <Node {...modalBodyContainer}>{content.content}</Node>
                <Node {...modalFooterContainer}>
                    <button onClick={() => setClose()} className='msc-modal__close-button'>
                        <span className='sr-only'>Close</span>
                    </button>
                </Node>
            </Module>
        </ModalContext.Provider>
    );
};

export const useModal = () => {
    const context = useContext(ModalContext);
    if (!context) {
        throw new Error('useModal must be used within the modal provider');
    }
    return context;
};
